import { i18nLocale } from "../lib/locale";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import {
  Plus, ChevronLeft, ChevronRight, X, Loader2, CheckCircle2,
  Calendar, Briefcase, Clock, TrendingUp, Trash2,
  Sparkles, Bell, HelpCircle, RefreshCcw,
} from "lucide-react";

/* ---------------- helpers ---------------- */
const WD = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const TYPE_HOURS = {
  full:      { start: "09:00", end: "17:00", label: "Journée complète" },
  morning:   { start: "09:00", end: "13:00", label: "Matin" },
  afternoon: { start: "13:00", end: "17:00", label: "Après-midi" },
};
const BLOCKED_REASON_COLOR = {
  "Congés":            "bg-[#FFE0DE] text-[#B23A2F] border-[#F5BFBA]",
  "Formation":         "bg-[#FFE5C2] text-[#7a5b18] border-[#F2C46B]",
  "Rendez-vous personnel": "bg-[#E4E7FF] text-[#3F4DA3] border-[#C5C9F2]",
  default:             "bg-[#F0F2EF] text-[#6B726A] border-[#E2E5E0]",
};

function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function parseISO(s) { const [y,m,d] = s.split("-").map(Number); return new Date(y, (m||1)-1, d||1); }
function frDate(s) { try { return parseISO(s).toLocaleDateString(i18nLocale(), { day: "numeric", month: "long", year: "numeric" }); } catch { return s; } }
function frDateShort(s) { try { return parseISO(s).toLocaleDateString(i18nLocale(), { day: "2-digit", month: "2-digit", year: "numeric" }); } catch { return s; } }
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function addMonths(d, n) { return new Date(d.getFullYear(), d.getMonth() + n, 1); }
function monthLabel(d) { return d.toLocaleDateString(i18nLocale(), { month: "long", year: "numeric" }); }
function eachDateBetween(startISO, endISO) {
  const out = [];
  let d = parseISO(startISO);
  const e = parseISO(endISO);
  while (d <= e) { out.push(isoDate(d)); d.setDate(d.getDate() + 1); }
  return out;
}
function uid() { return Math.random().toString(36).slice(2, 10); }

/* ---------------- Page ---------------- */
export default function MyAvailabilities() {
  const { user, refreshUser } = useAuth();
  const [cursor, setCursor] = useState(startOfMonth(new Date()));
  const [selected, setSelected] = useState(new Set()); // currently being added
  const [stats, setStats] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [blockedOpen, setBlockedOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // { date, slot } pour dispo récurrente
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState("");

  const slots = user?.availability_slots || [];
  const blocked = user?.blocked_periods || [];
  const [calendarEntries, setCalendarEntries] = useState([]); // bookings overlay

  const entriesByDate = useMemo(() => {
    const m = new Map();
    for (const e of calendarEntries) {
      if (e.kind === "mission_confirmed" || e.kind === "mission_proposed" || e.kind === "mission_completed") {
        if (!m.has(e.date)) m.set(e.date, []);
        m.get(e.date).push(e);
      }
    }
    return m;
  }, [calendarEntries]);

  const slotsByDate = useMemo(() => {
    const m = new Map();
    for (const s of slots) if (s?.date) m.set(s.date, s); // dedupe by date
    return m;
  }, [slots]);

  const blockedDatesSet = useMemo(() => {
    const set = new Set();
    for (const p of blocked) for (const d of eachDateBetween(p.start_date, p.end_date)) set.add(d);
    return set;
  }, [blocked]);

  // Load stats
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, c] = await Promise.all([
          api.get("/availability/stats"),
          api.get("/calendar/me"),
        ]);
        if (!cancelled) {
          setStats(s.data);
          setCalendarEntries(c.data?.entries || []);
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [user?.id, slots.length, blocked.length]);

  const grid = useMemo(() => {
    const first = startOfMonth(cursor);
    const firstWd = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstWd; i++) {
      const pad = new Date(cursor.getFullYear(), cursor.getMonth(), -firstWd + i + 1);
      cells.push({ date: pad, muted: true });
    }
    for (let d = 1; d <= daysInMonth; d++) cells.push({ date: new Date(cursor.getFullYear(), cursor.getMonth(), d), muted: false });
    while (cells.length % 7 !== 0) {
      const last = cells[cells.length - 1].date;
      cells.push({ date: new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1), muted: true });
    }
    return cells;
  }, [cursor]);

  const today = isoDate(new Date());
  const toggle = (d) => {
    if (!d) return;
    const k = isoDate(d);
    if (k < today) return;
    if (blockedDatesSet.has(k)) return;
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });
  };

  // Save slots
  const saveSlots = async (newSlots) => {
    setBusy(true);
    try {
      await api.patch("/auth/profile", { availability_slots: newSlots });
      await refreshUser();
    } finally { setBusy(false); }
  };
  const saveBlocked = async (newBlocked) => {
    setBusy(true);
    try {
      await api.patch("/auth/profile", { blocked_periods: newBlocked });
      await refreshUser();
    } finally { setBusy(false); }
  };

  const addSelectedSlots = async ({ type, start, end, mode, weekdays = [] }) => {
    const map = new Map(slotsByDate);
    let dates;
    if (mode === "permanent") {
      // Répétition hebdomadaire sur les jours choisis, à partir de la première date sélectionnée (12 mois)
      dates = new Set();
      const first = Array.from(selected).sort()[0];
      const base = parseISO(first);
      for (let i = 0; i <= 365; i++) {
        const nd = new Date(base);
        nd.setDate(base.getDate() + i);
        if (weekdays.includes((nd.getDay() + 6) % 7)) dates.add(isoDate(nd));
      }
    } else {
      dates = new Set(selected);
    }
    const today = isoDate(new Date());
    for (const d of dates) {
      if (d < today || blockedDatesSet.has(d)) continue;
      map.set(d, { date: d, type, start, end, recurring: mode === "permanent" });
    }
    await saveSlots(Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date)));
    setSelected(new Set());
    setAddOpen(false);
    showFlash(mode === "permanent" ? "Disponibilité permanente ajoutée (répétée chaque semaine)" : "Disponibilités ajoutées");
  };

  const removeSlot = async (dateISO) => {
    const newSlots = slots.filter((s) => s.date !== dateISO);
    await saveSlots(newSlots);
  };

  const removeSeries = async (slot) => {
    const newSlots = slots.filter((s) => !(s.recurring && s.start === slot.start && s.end === slot.end));
    await saveSlots(newSlots);
    showFlash("Disponibilité permanente supprimée");
  };

  const addBlocked = async ({ start_date, end_date, reason }) => {
    const newBlocked = [...blocked, { id: uid(), start_date, end_date, reason }]
      .sort((a, b) => a.start_date.localeCompare(b.start_date));
    await saveBlocked(newBlocked);
    setBlockedOpen(false);
    showFlash("Période bloquée ajoutée");
  };
  const removeBlocked = async (id) => { await saveBlocked(blocked.filter((b) => b.id !== id)); };

  const showFlash = (m) => { setFlash(m); setTimeout(() => setFlash(""), 2200); };

  return (
    <div className="max-w-[1440px] mx-auto p-4 lg:p-8 pb-24 lg:pb-10">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
        <div>
          <h1 className="text-3xl sm:text-4xl font-medium tracking-tight text-[#0F2A1B]" style={{ fontFamily: "Outfit" }}>Mon planning &amp; disponibilités</h1>
          <p className="text-sm text-[#6B726A] mt-1">Gérez vos disponibilités et vos périodes bloquées.</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="w-10 h-10 rounded-full bg-white border border-[#E2E5E0] grid place-items-center text-[#1A4D2E]" data-testid="avail-bell"><Bell className="w-4 h-4" /></button>
          <button className="w-10 h-10 rounded-full bg-white border border-[#E2E5E0] grid place-items-center text-[#6B726A]" data-testid="avail-help"><HelpCircle className="w-4 h-4" /></button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4 mb-6">
        <KpiCard
          testid="kpi-status"
          title="Disponibilité actuelle"
          value={
            <span className="inline-flex items-center gap-2">
              <span className={`w-2.5 h-2.5 rounded-full ${stats?.current_status === "available" ? "bg-[#1A8F4A]" : stats?.current_status === "blocked" ? "bg-[#E3655B]" : "bg-[#C5CBC2]"}`} />
              <span className={`text-2xl font-medium ${stats?.current_status === "available" ? "text-[#1A8F4A]" : stats?.current_status === "blocked" ? "text-[#E3655B]" : "text-[#6B726A]"}`}>
                {stats?.current_status === "available" ? "Disponible" : stats?.current_status === "blocked" ? "Bloqué" : "Indisponible"}
              </span>
            </span>
          }
          footer={
            <div>
              <div className="flex items-center justify-between text-[11px] text-[#6B726A] mb-1">
                <span>Taux de disponibilité</span><span className="font-medium text-[#0F2A1B]">{stats?.availability_rate ?? 0}%</span>
              </div>
              <div className="h-1.5 bg-[#F0F2EF] rounded-full overflow-hidden"><div className="h-full bg-[#1A4D2E]" style={{ width: `${stats?.availability_rate ?? 0}%` }} /></div>
            </div>
          }
        />
        <KpiCard testid="kpi-next" title="Prochaine disponibilité"
          icon={<Calendar className="w-4 h-4 text-[#1A4D2E]" />}
          value={<span className="text-2xl font-medium text-[#0F2A1B]">{stats?.next_available?.date ? frDate(stats.next_available.date) : "—"}</span>}
          footer={<span className="text-xs text-[#6B726A]">{stats?.next_available ? TYPE_HOURS[stats.next_available.type]?.label || "Personnalisée" : "Aucune date programmée"}</span>}
        />
        <KpiCard testid="kpi-missions" title="Missions planifiées"
          icon={<Briefcase className="w-4 h-4 text-[#1A4D2E]" />}
          value={<span className="text-2xl font-medium text-[#0F2A1B]">{stats?.missions_planned_count ?? 0} {stats?.missions_planned_count === 1 ? "jour" : "jours"}</span>}
          footer={<span className="text-xs text-[#6B726A]">Ce mois-ci</span>}
        />
        <KpiCard testid="kpi-hours" title="Heures travaillées"
          icon={<Clock className="w-4 h-4 text-[#1A4D2E]" />}
          value={<span className="text-2xl font-medium text-[#0F2A1B]">{stats?.hours_worked ?? 0} h</span>}
          footer={<span className="text-xs text-[#6B726A]">Ce mois-ci</span>}
        />
        <button onClick={() => showFlash("Statistiques : bientôt disponibles")} data-testid="kpi-stats" className="bg-gradient-to-br from-[#F4F0FF] to-[#EDE6FF] border border-[#D9CCFB] rounded-2xl p-5 text-left hover:brightness-105 transition-all">
          <TrendingUp className="w-5 h-5 text-[#6B4DD9] mb-2" />
          <div className="text-base font-medium text-[#3F2EB2]">Voir mes stats</div>
          <div className="text-xs text-[#6B4DD9]/80 mt-0.5">Analyses détaillées</div>
        </button>
      </div>

      {/* Main grid */}
      <div className="space-y-6">
        {/* Calendar column */}
        <div className="bg-white rounded-2xl border border-[#E2E5E0] p-4 sm:p-6">
          {/* Calendar toolbar */}
          <div className="flex items-center justify-end flex-wrap gap-3 mb-4">
            <div className="flex items-center gap-2">
              <button onClick={() => setCursor(addMonths(cursor, -1))} className="w-8 h-8 rounded-full border border-[#E2E5E0] hover:bg-[#F7F8F6] grid place-items-center" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></button>
              <div className="text-base font-medium capitalize text-[#0F2A1B] min-w-[160px] text-center" style={{ fontFamily: "Outfit" }} data-testid="cal-month-label">{monthLabel(cursor)}</div>
              <button onClick={() => setCursor(addMonths(cursor, 1))} className="w-8 h-8 rounded-full border border-[#E2E5E0] hover:bg-[#F7F8F6] grid place-items-center" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></button>
              <button onClick={() => setCursor(startOfMonth(new Date()))} className="px-3 py-1.5 text-xs rounded-full border border-[#E2E5E0] hover:bg-[#F7F8F6]" data-testid="cal-today">Aujourd&apos;hui</button>
              <button onClick={() => { if (selected.size === 0) { showFlash("Sélectionnez d'abord des jours"); return; } setAddOpen(true); }} className="px-4 py-1.5 text-xs rounded-full bg-[#1A4D2E] text-white hover:bg-[#143C24] inline-flex items-center gap-1" data-testid="cal-add">
                <Plus className="w-3.5 h-3.5" /> Ajouter
              </button>
            </div>
          </div>

          <>
              {/* Weekday header */}
              <div className="grid grid-cols-7 gap-1 mb-1">
                {WD.map((w) => <div key={w} className="text-[11px] uppercase tracking-wider text-[#6B726A] text-center py-1.5">{w}</div>)}
              </div>
              {/* Days */}
              <div className="grid grid-cols-7 gap-1">
                {grid.map((c, i) => {
                  const d = c.date;
                  const k = isoDate(d);
                  const isPast = k < today;
                  const isToday = k === today;
                  const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                  const slot = slotsByDate.get(k);
                  const isBlocked = blockedDatesSet.has(k);
                  const isSel = selected.has(k);
                  const dayEntries = entriesByDate.get(k) || [];
                  const hasCompleted = dayEntries.some(e => e.kind === "mission_completed");
                  const hasConfirmed = dayEntries.some(e => e.kind === "mission_confirmed");
                  const hasProposed = !hasCompleted && !hasConfirmed && dayEntries.some(e => e.kind === "mission_proposed");
                  const bg = c.muted
                    ? "bg-[#FAFAFA] text-[#C5CBC2]"
                    : hasCompleted
                      ? "bg-[#EDE9FE] border border-[#C4B5FD] text-[#4C1D95]"
                      : hasConfirmed
                        ? "bg-[#DBEAFE] border border-[#93C5FD] text-[#1E3A8A]"
                        : hasProposed
                          ? "bg-[#FEF3C7] border border-[#FCD34D] text-[#92400E]"
                          : isBlocked
                            ? "bg-[#FDE4E2] text-[#A53A2F] border border-[#F5BFBA]"
                            : slot
                              ? (slot.recurring
                                ? "bg-[#E0F2F1] border border-[#7FCBC4] text-[#0F2A1B]"
                                : "bg-[#E6F4EB] border border-[#A8D9B6] text-[#0F2A1B]")
                              : isWeekend
                                ? "bg-[#FFF2EE] text-[#A53A2F] border border-[#F5BFBA]"
                                : "bg-[#F2FAF4] text-[#0F2A1B] border border-[#E2E5E0]";
                  return (
                    <button
                      key={`${k}-${i}`}
                      onClick={() => !c.muted && toggle(d)}
                      disabled={c.muted || isPast || isBlocked || hasConfirmed || hasCompleted}
                      data-testid={`day-${k}`}
                      className={`relative min-h-[88px] rounded-xl text-sm transition-all text-left p-2
                        ${bg}
                        ${isSel ? "ring-2 ring-[#1A4D2E] ring-offset-1" : ""}
                        ${isToday && !isSel ? "ring-1 ring-[#1A4D2E]/60" : ""}
                        ${(isPast || c.muted || isBlocked || hasConfirmed || hasCompleted) ? "cursor-not-allowed opacity-90" : "hover:brightness-95"}`}>
                      <div className="font-medium">{d.getDate()}</div>
                      {/* Mission overlay takes priority over slot */}
                      {hasCompleted ? (
                        <div className="mt-1.5">
                          <div className="w-5 h-5 rounded-full bg-[#7C3AED] grid place-items-center mb-1">
                            <Briefcase className="w-3 h-3 text-white" />
                          </div>
                          <div className="text-[10px] font-medium truncate" title={dayEntries[0].title}>Terminée</div>
                          <div className="text-[10px] opacity-80">{dayEntries[0].start_time} - {dayEntries[0].end_time}</div>
                        </div>
                      ) : hasConfirmed ? (
                        <div className="mt-1.5">
                          <div className="w-5 h-5 rounded-full bg-[#3B82F6] grid place-items-center mb-1">
                            <Briefcase className="w-3 h-3 text-white" />
                          </div>
                          <div className="text-[10px] font-medium truncate" title={dayEntries[0].title}>{dayEntries[0].title}</div>
                          <div className="text-[10px] opacity-80">{dayEntries[0].start_time} - {dayEntries[0].end_time}</div>
                        </div>
                      ) : hasProposed ? (
                        <div className="mt-1.5">
                          <div className="w-5 h-5 rounded-full bg-[#F59E0B] grid place-items-center mb-1">
                            <Sparkles className="w-3 h-3 text-white" />
                          </div>
                          <div className="text-[10px] font-medium truncate" title={dayEntries[0].title}>Proposée</div>
                          <div className="text-[10px] opacity-80">{dayEntries[0].start_time} - {dayEntries[0].end_time}</div>
                        </div>
                      ) : slot ? (
                        <>
                          <span
                            role="button"
                            data-testid={`remove-slot-${k}`}
                            title="Retirer cette disponibilité"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); slot.recurring ? setConfirmDelete({ date: k, slot }) : removeSlot(k); }}
                            className="absolute top-1 right-1 w-5 h-5 rounded-full grid place-items-center text-[#E3655B] hover:bg-[#FFF4F3]">
                            <Trash2 className="w-3 h-3" />
                          </span>
                          <div className="mt-1.5">
                            <div className={`w-5 h-5 rounded-full grid place-items-center mb-1 ${slot.recurring ? "bg-[#0E8D77]" : "bg-[#1A8F4A]"}`}>
                              {slot.recurring ? <RefreshCcw className="w-3 h-3 text-white" /> : <CheckCircle2 className="w-3 h-3 text-white" />}
                            </div>
                            <div className={`text-[10px] font-medium ${slot.recurring ? "text-[#0E6E5D]" : "text-[#1A4D2E]"}`}>{slot.start} - {slot.end}</div>
                            {slot.recurring && <div className="text-[9px] text-[#0E6E5D]/80">Permanent</div>}
                          </div>
                        </>
                      ) : isBlocked ? (
                        <div className="mt-1 text-[10px] font-medium text-[#A53A2F]">Bloqué</div>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </>

          {/* Legend */}
          <div className="mt-6 pt-4 border-t border-dashed border-[#E2E5E0] flex flex-wrap gap-4 text-[11px] text-[#6B726A]">
            <Legend color="#1A8F4A" label="Disponible (temporaire)" />
            <Legend color="#0E8D77" label="Disponible (permanente)" />
            <Legend color="#3B82F6" label="Mission confirmée" />
            <Legend color="#F59E0B" label="Mission proposée" />
            <Legend color="#7C3AED" label="Mission terminée" />
            <Legend color="#E3655B" label="Indisponible / Bloqué" />
          </div>

          {/* Quick add bottom + Périodes bloquées côte à côte */}
          <div className="mt-6 grid sm:grid-cols-2 gap-3 items-start">
            <div>
              <div className="text-sm font-medium text-[#0F2A1B] mb-3" style={{ fontFamily: "Outfit" }}>Ajouter rapidement</div>
              <QuickAddCard tone="green" title="Disponible" desc="Sélectionnez des jours dans le calendrier" cta="+ Disponibilité" onClick={() => { if (selected.size === 0) showFlash("Cliquez d'abord sur des jours"); else setAddOpen(true); }} testid="quick-add-avail" />
            </div>
            <div>
              <div className="text-sm font-medium text-[#0F2A1B] mb-3" style={{ fontFamily: "Outfit" }}>Périodes bloquées</div>
              <div className="rounded-2xl p-4 border bg-[#FDE4E2] border-[#F5BFBA]" data-testid="panel-blocked">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="text-xs text-[#6B726A]">
                    {blocked.length === 0 ? "Aucune période bloquée." : `${blocked.length} période(s) bloquée(s)`}
                  </div>
                  <button onClick={() => setBlockedOpen(true)} className="px-4 py-1.5 text-xs font-medium rounded-full border bg-white border-[#E3655B] text-[#E3655B] hover:bg-[#FFD7D2] shrink-0" data-testid="add-blocked-btn">+ Bloquer une période</button>
                </div>
                {blocked.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {blocked.slice().sort((a,b)=>a.start_date.localeCompare(b.start_date)).map((p) => (
                      <div key={p.id} className="flex items-center justify-between gap-2 py-1 text-xs" data-testid={`blocked-${p.id}`}>
                        <div className="text-[#0F2A1B] flex items-center gap-1.5">
                          <span>{frDateShort(p.start_date)}</span><span className="text-[#6B726A]">→</span><span>{frDateShort(p.end_date)}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] border ${BLOCKED_REASON_COLOR[p.reason] || BLOCKED_REASON_COLOR.default}`}>{p.reason || "—"}</span>
                          <button onClick={() => removeBlocked(p.id)} className="w-6 h-6 rounded grid place-items-center hover:bg-[#FFF4F3] text-[#E3655B]" data-testid={`remove-blocked-${p.id}`}><Trash2 className="w-3 h-3" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {addOpen && (
        <AddAvailabilityModal
          selectedDates={Array.from(selected).sort()}
          onClose={() => setAddOpen(false)}
          onConfirm={addSelectedSlots}
          busy={busy}
        />
      )}
      {blockedOpen && (
        <BlockPeriodModal
          onClose={() => setBlockedOpen(false)}
          onConfirm={addBlocked}
          busy={busy}
        />
      )}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/30 grid place-items-center z-[60] p-4" onClick={() => setConfirmDelete(null)}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()} data-testid="delete-recurring-modal">
            <h3 className="text-base font-medium text-[#0F2A1B] mb-1" style={{ fontFamily: "Outfit" }}>Disponibilité permanente</h3>
            <p className="text-xs text-[#6B726A] mb-4">
              Le {frDateShort(confirmDelete.date)} fait partie d'une disponibilité permanente ({confirmDelete.slot.start} - {confirmDelete.slot.end}). Que souhaitez-vous supprimer ?
            </p>
            <div className="space-y-2">
              <button
                onClick={async () => { await removeSlot(confirmDelete.date); setConfirmDelete(null); }}
                disabled={busy} data-testid="delete-single-day"
                className="w-full text-sm px-4 py-2.5 rounded-full border border-[#E2E5E0] text-[#0F2A1B] hover:bg-[#F7F8F6] disabled:opacity-60">
                Supprimer ce jour uniquement
              </button>
              <button
                onClick={async () => { await removeSeries(confirmDelete.slot); setConfirmDelete(null); }}
                disabled={busy} data-testid="delete-whole-series"
                className="w-full text-sm px-4 py-2.5 rounded-full bg-[#E3655B] text-white hover:bg-[#C94F45] disabled:opacity-60">
                Supprimer toute la série
              </button>
              <button onClick={() => setConfirmDelete(null)} data-testid="delete-cancel"
                className="w-full text-sm px-4 py-2 text-[#6B726A] hover:text-[#0F2A1B]">
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Flash toast */}
      {flash && (
        <div className="fixed bottom-6 right-6 z-50 bg-[#1A4D2E] text-white text-sm px-4 py-2.5 rounded-xl shadow-lg inline-flex items-center gap-2 animate-fade-in" data-testid="flash-toast">
          <CheckCircle2 className="w-4 h-4" /> {flash}
        </div>
      )}

      {/* Helper: list slot deletion via long-press? On click instead, double click below. */}

      <style>{`
        @keyframes fade-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fade-in { animation: fade-in 180ms ease-out; }
      `}</style>
    </div>
  );
}

/* ---------------- Sub-components ---------------- */
function KpiCard({ title, value, footer, icon, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E2E5E0] p-5" data-testid={testid}>
      <div className="flex items-start gap-2 text-xs text-[#6B726A] mb-2">
        {icon && <div className="w-7 h-7 rounded-lg bg-[#E8F0EA] grid place-items-center">{icon}</div>}
        <span className="mt-1.5">{title}</span>
      </div>
      <div className="mb-2">{value}</div>
      {footer}
    </div>
  );
}

function Legend({ color, label }) {
  return <span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} /> {label}</span>;
}

function QuickAddCard({ tone, title, desc, cta, onClick, testid }) {
  const bg = tone === "green" ? "bg-[#E6F4EB] border-[#A8D9B6]" : "bg-[#FDE4E2] border-[#F5BFBA]";
  const btn = tone === "green" ? "border-[#1A8F4A] text-[#1A8F4A] hover:bg-[#D5EFDD]" : "border-[#E3655B] text-[#E3655B] hover:bg-[#FFD7D2]";
  return (
    <div className={`rounded-2xl p-4 border ${bg}`}>
      <div className="text-sm font-medium text-[#0F2A1B] mb-0.5" style={{ fontFamily: "Outfit" }}>{title}</div>
      <div className="text-xs text-[#6B726A] mb-3">{desc}</div>
      <button onClick={onClick} data-testid={testid} className={`px-4 py-1.5 text-xs font-medium rounded-full border bg-white ${btn}`}>{cta}</button>
    </div>
  );
}

/* ---------------- Modals ---------------- */
function AddAvailabilityModal({ selectedDates, onClose, onConfirm, busy }) {
  const WEEKDAY_FULL = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
  const [mode, setMode] = useState("temporary");
  const [days, setDays] = useState(() => [...new Set(selectedDates.map((d) => (parseISO(d).getDay() + 6) % 7))]);
  const toggleDay = (i) => setDays((prev) => prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i].sort());
  const [type, setType] = useState("full");
  const [customStart, setCustomStart] = useState("09:00");
  const [customEnd, setCustomEnd] = useState("17:00");
  const start = type === "custom" ? customStart : TYPE_HOURS[type].start;
  const end = type === "custom" ? customEnd : TYPE_HOURS[type].end;
  const first = selectedDates[0]; const last = selectedDates[selectedDates.length - 1];
  const rangeLabel = first && last && first !== last
    ? `Du ${frDate(first)} au ${frDate(last)}`
    : (first ? frDate(first) : "");
  return (
    <div className="fixed inset-0 bg-black/30 grid place-items-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="add-avail-modal">
        <div className="flex items-start justify-between mb-1">
          <h3 className="text-base font-medium text-[#0F2A1B]" style={{ fontFamily: "Outfit" }}>Ajouter une disponibilité</h3>
          <button onClick={onClose} className="w-7 h-7 grid place-items-center text-[#6B726A] hover:text-[#0F2A1B]" data-testid="modal-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="text-xs uppercase tracking-wider font-medium text-[#6B726A] mb-2">Type de disponibilité</div>
        <div className="grid grid-cols-2 gap-2 mb-4" data-testid="avail-mode-picker">
          {[
            { k: "temporary", label: "Temporaire", desc: "Pour une date précise" },
            { k: "permanent", label: "Permanente", desc: "Se répète chaque semaine sur les jours choisis" },
          ].map((m) => (
            <button key={m.k} type="button" data-testid={`avail-mode-${m.k}`}
              onClick={() => setMode(m.k)}
              className={`text-left px-3 py-3 rounded-2xl border-2 transition-colors ${
                mode === m.k ? "border-[#1A4D2E] bg-[#E8F0EA]" : "border-[#E2E5E0] hover:border-[#1A4D2E]/40"
              }`}>
              <span className="flex items-center gap-2 text-sm font-medium text-[#0F2A1B]">
                <span className={`w-4 h-4 rounded-full border-2 grid place-items-center shrink-0 ${mode === m.k ? "border-[#1A4D2E]" : "border-[#C5CBC2]"}`}>
                  {mode === m.k && <span className="w-2 h-2 rounded-full bg-[#1A4D2E]" />}
                </span>
                {m.label}
              </span>
              <span className="block text-[11px] text-[#6B726A] mt-0.5 ml-6">{m.desc}</span>
            </button>
          ))}
        </div>

        <div className="text-xs text-[#6B726A] mb-4">
          <div>{selectedDates.length} {selectedDates.length > 1 ? "jours sélectionnés" : "jour sélectionné"}</div>
          {rangeLabel && <div className="font-medium text-[#0F2A1B] mt-0.5">{rangeLabel}</div>}
        </div>

        {mode === "permanent" && (
          <>
            <div className="text-xs uppercase tracking-wider font-medium text-[#6B726A] mb-2">Jours de la semaine</div>
            <div className="flex flex-wrap gap-1.5 mb-3" data-testid="perm-weekdays">
              {WEEKDAY_FULL.map((label, i) => (
                <button key={label} type="button" onClick={() => toggleDay(i)}
                  data-testid={`perm-day-${i}`}
                  className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                    days.includes(i)
                      ? "bg-[#E0F2F1] border-[#0E8D77] text-[#0E6E5D] font-medium"
                      : "border-[#E2E5E0] text-[#6B726A] hover:border-[#0E8D77]/40"
                  }`}>
                  {label}
                </button>
              ))}
            </div>
          </>
        )}

        {mode !== "permanent" && (
          <>
            <div className="text-xs uppercase tracking-wider font-medium text-[#6B726A] mb-2">Horaires</div>
            <div className="space-y-1 mb-4">
              {["full","morning","afternoon","custom"].map((t) => (
                <label key={t} className={`flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl border cursor-pointer ${type === t ? "border-[#1A4D2E] bg-[#E8F0EA]" : "border-[#E2E5E0] hover:bg-[#F7F8F6]"}`} data-testid={`type-${t}`}>
                  <span className="flex items-center gap-2 text-sm text-[#0F2A1B]">
                    <span className={`w-4 h-4 rounded-full border-2 grid place-items-center ${type === t ? "border-[#1A4D2E]" : "border-[#C5CBC2]"}`}>
                      {type === t && <span className="w-2 h-2 rounded-full bg-[#1A4D2E]" />}
                    </span>
                    {t === "custom" ? "Personnalisée" : TYPE_HOURS[t]?.label}
                  </span>
                  <span className="text-xs text-[#6B726A]">{t === "custom" ? `${customStart} - ${customEnd}` : `${TYPE_HOURS[t].start} - ${TYPE_HOURS[t].end}`}</span>
                  <input type="radio" name="avail-type" className="hidden" checked={type === t} onChange={() => setType(t)} />
                </label>
              ))}
            </div>

            {type === "custom" && (
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xs text-[#6B726A]">De</span>
                <input type="time" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="text-xs px-2 py-1 rounded border border-[#E2E5E0] flex-1" data-testid="custom-start" />
                <span className="text-xs text-[#6B726A]">à</span>
                <input type="time" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="text-xs px-2 py-1 rounded border border-[#E2E5E0] flex-1" data-testid="custom-end" />
              </div>
            )}
          </>
        )}

        <div className="text-xs px-3 py-2 rounded-lg bg-[#F4F0FF] border border-[#D9CCFB] text-[#3F2EB2] mb-4 inline-flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" />
          {mode === "permanent"
            ? "Cette disponibilité sera répétée chaque semaine jusqu'à sa modification ou sa suppression."
            : "Ces créneaux seront ajoutés comme disponibles"}
        </div>

        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 text-sm px-4 py-2.5 rounded-full border border-[#E2E5E0] text-[#6B726A] hover:bg-[#F7F8F6]" data-testid="modal-cancel">Annuler</button>
          <button onClick={() => onConfirm(mode === "permanent"
              ? { type: "full", start: TYPE_HOURS.full.start, end: TYPE_HOURS.full.end, mode, weekdays: days }
              : { type, start, end, mode })}
            disabled={busy || selectedDates.length === 0 || (mode === "permanent" && days.length === 0)} data-testid="modal-confirm"
            className="flex-1 text-sm px-4 py-2.5 rounded-full bg-[#1A4D2E] text-white hover:bg-[#143C24] disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Ajouter"}
          </button>
        </div>
      </div>
    </div>
  );
}

function BlockPeriodModal({ onClose, onConfirm, busy }) {
  const [start, setStart] = useState(isoDate(new Date()));
  const [end, setEnd] = useState(isoDate(new Date()));
  const [reason, setReason] = useState("Congés");
  const valid = start && end && start <= end;
  return (
    <div className="fixed inset-0 bg-black/30 grid place-items-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="block-modal">
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-base font-medium text-[#0F2A1B]" style={{ fontFamily: "Outfit" }}>Bloquer une période</h3>
          <button onClick={onClose} className="w-7 h-7 grid place-items-center text-[#6B726A]" data-testid="block-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <div className="text-xs uppercase tracking-wider font-medium text-[#6B726A] mb-1">Du</div>
            <input type="date" value={start} min={isoDate(new Date())} onChange={(e) => setStart(e.target.value)} className="w-full text-sm px-3 py-2 rounded-xl border border-[#E2E5E0]" data-testid="block-start" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider font-medium text-[#6B726A] mb-1">Au</div>
            <input type="date" value={end} min={start} onChange={(e) => setEnd(e.target.value)} className="w-full text-sm px-3 py-2 rounded-xl border border-[#E2E5E0]" data-testid="block-end" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider font-medium text-[#6B726A] mb-1">Motif</div>
            <select value={reason} onChange={(e) => setReason(e.target.value)} className="w-full text-sm px-3 py-2 rounded-xl border border-[#E2E5E0]" data-testid="block-reason">
              <option>Congés</option>
              <option>Formation</option>
              <option>Rendez-vous personnel</option>
              <option>Indisponible</option>
            </select>
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 text-sm px-4 py-2.5 rounded-full border border-[#E2E5E0] text-[#6B726A] hover:bg-[#F7F8F6]" data-testid="block-cancel">Annuler</button>
          <button disabled={!valid || busy} onClick={() => onConfirm({ start_date: start, end_date: end, reason })} data-testid="block-confirm"
            className="flex-1 text-sm px-4 py-2.5 rounded-full bg-[#E3655B] text-white hover:bg-[#C8554C] disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Bloquer"}
          </button>
        </div>
      </div>
    </div>
  );
}
